import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, User as UserIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Logo } from '@/components/common/Logo';
import { useSession } from './session';
import { loginRequest, LoginError } from './api';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const setSession = useSession((s) => s.setSession);
  const navigate = useNavigate();
  const location = useLocation();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await loginRequest({ username, password });
      setSession(result);
      const to =
        (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
        '/dashboard';
      navigate(to, { replace: true });
    } catch (e) {
      if (e instanceof LoginError) {
        setError(e.message);
      } else if ((e as { code?: string }).code === 'ERR_NETWORK') {
        setError(
          "We couldn't reach the authentication server. Check your connection and try again.",
        );
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background:
          'linear-gradient(135deg, #E4F6F9 0%, #F8FAFC 60%, #FFFFFF 100%)',
      }}
    >
      <div
        className="w-full max-w-[400px] rounded-2xl bg-white p-8"
        style={{ boxShadow: '0 20px 50px rgba(18, 23, 90, 0.10)' }}
      >
        <div className="flex flex-col items-center mb-6">
          <Logo variant="full" className="w-32" />
          <h1 className="mt-4 text-h2 text-ink">Back Office</h1>
          <p className="mt-1 text-body text-ink-muted">
            Sign in to manage customer requests
          </p>
        </div>

        {error && (
          <div className="mb-5 flex items-start gap-2 rounded-lg bg-status-error-bg px-3 py-2.5 text-body text-status-error">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span className="leading-snug">{error}</span>
          </div>
        )}

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Input
            label="Username"
            placeholder="Mobile number"
            autoComplete="username"
            iconLeft={<UserIcon className="h-4 w-4" />}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={submitting}
            required
          />
          <Input
            label="Password"
            type={showPwd ? 'text' : 'password'}
            placeholder="Enter your password"
            autoComplete="current-password"
            iconLeft={<Lock className="h-4 w-4" />}
            iconRight={
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="rounded p-0.5 text-ink-muted hover:bg-canvas"
                aria-label={showPwd ? 'Hide password' : 'Show password'}
              >
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            required
          />

          <div className="flex items-center justify-between text-caption">
            <Checkbox
              label="Keep me signed in"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedIn(e.target.checked)}
            />
            <a
              href="#"
              className="text-brand-blue font-medium hover:underline"
              onClick={(e) => e.preventDefault()}
            >
              Forgot password?
            </a>
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full mt-2"
            loading={submitting}
          >
            Sign in
          </Button>
        </form>

        <p className="mt-6 text-center text-caption text-ink-hint">
          © KIWASCO {new Date().getFullYear()} · v1.0
        </p>
      </div>
    </div>
  );
}
